import React from "react";
import { cva, VariantProps } from "class-variance-authority";
import { cn } from "@/shared/utils/shadcn";
import { Icon, IconName, IconColor } from "@/shared/ui/Icon";

type ChipColor =
  | "primary"
  | "secondary"
  | "green"
  | "orange"
  | "grey"
  | "yellow"
  | "red"
  | "purple"
  | "blue";

const chipVariants = cva(
  "flex justify-center whitespace-nowrap items-center shrink-0 grow-0 h-fit border-solid rounded-md font-medium tracking-widest",
  {
    variants: {
      variant: {
        solid: "text-white border-primary",
        outlined: "bg-white text-primary border-primary border",
        contrast: "text-primary bg-primary/30 border-primary border",
      },
      color: {
        primary: "bg-primary border-primary",
        secondary: "bg-secondary border-secondary-500",
        green: "bg-green-500 border-green-500",
        orange: "bg-orange-500 border-orange-500",
        grey: "bg-grey-500 border-grey-500",
        yellow: "bg-yellow-500 border-yellow-500",
        red: "bg-red-500 border-red-500",
        purple: "bg-purple-500 border-purple-500",
        blue: "bg-blue-500 border-blue-500",
      },
      size: {
        sm: "text-[10px] px-1 gap-1",
        md: "text-xs px-2 py-1 gap-1.5",
        lg: "text-sm px-2 py-1 gap-2",
      },
    },
    compoundVariants: [
      {
        variant: "contrast",
        color: "blue",
        className: "text-blue-500 bg-blue-100  border-none",
      },
      {
        variant: "contrast",
        color: "grey",
        className: "text-grey-500 bg-grey-100  border-none",
      },
      {
        variant: "contrast",
        color: "orange",
        className: "text-orange-500 bg-orange-100  border-none",
      },
      {
        variant: "outlined",
        color: "primary",
        className: "text-primary bg-white border-primary",
      },
      {
        variant: "outlined",
        color: "secondary",
        className: "text-secondary bg-white border-secondary-500",
      },
      {
        variant: "outlined",
        color: "green",
        className: "text-green-500 bg-white border-green-500",
      },
      {
        variant: "outlined",
        color: "orange",
        className: "text-orange-500 bg-white border-orange-500",
      },
      {
        variant: "outlined",
        color: "grey",
        className: "text-grey-500 bg-white border-grey-500",
      },
      {
        variant: "outlined",
        color: "yellow",
        className: "text-yellow-500 bg-white border-yellow-500",
      },
      {
        variant: "outlined",
        color: "red",
        className: "text-red-500 bg-white border-red-500",
      },
      {
        variant: "outlined",
        color: "purple",
        className: "text-purple-500 bg-white border-purple-500",
      },
      {
        variant: "outlined",
        color: "blue",
        className: "text-blue-500 bg-white border-blue-500",
      },
    ],
    defaultVariants: {
      variant: "solid",
      color: "primary",
      size: "md",
    },
  }
);

interface ChipProps extends VariantProps<typeof chipVariants> {
  color?: ChipColor;
  children: React.ReactNode;
  className?: string;
  icon?: IconName;
}

/**
 * Determines the appropriate icon color based on chip variant and color
 */
const getChipIconColor = (
  variant: "solid" | "outlined" | "contrast" | null | undefined,
  color: ChipColor
): IconColor => {
  const resolvedVariant = variant || "solid";

  if (resolvedVariant === "solid") {
    return "white";
  }

  if (resolvedVariant === "contrast") {
    // For contrast variant, use the color that matches the background
    if (color === "blue") return "blue";
    if (color === "grey") return "grey";
    if (color === "orange") return "orange";
    return "primary";
  }

  // For outlined variant, icon color matches the border/text color
  // Note: "secondary" maps to "primary" as it's not available in IconColor
  const colorMap: Record<
    ChipColor,
    "primary" | "green" | "orange" | "grey" | "red" | "purple" | "blue"
  > = {
    primary: "primary",
    secondary: "primary", // secondary not available in IconColor, use primary
    green: "green",
    orange: "orange",
    grey: "grey",
    yellow: "orange", // yellow maps to orange for icon
    red: "red",
    purple: "purple",
    blue: "blue",
  };

  return colorMap[color] || "primary";
};

const Chip: React.FC<ChipProps> = ({
  color = "primary",
  variant = "solid",
  size = "md",
  children,
  icon,
  className,
  ...rest
}) => {
  const styles = chipVariants({
    variant: variant as any,
    color: color as any,
    size: size as any,
  });

  const iconColor = getChipIconColor(variant, color);
  const iconSize = size === "sm" ? "2xs" : size === "md" ? "xs" : "sm";

  return (
    <div className={cn(styles, className)} {...rest}>
      {icon && <Icon icon={icon} size={iconSize} color={iconColor} />}
      {children}
    </div>
  );
};

export default Chip;
